// Collector's Vault layout primitives — the dashboard's visual language,
// extracted so every screen shares one system instead of per-page inline
// styles. Prefer these over hardcoded gradients / gold hex / Shapiro strings.
//
//   <PageShell eyebrow="Vault" title="My Collection" subtitle="…" actions={…}>
//     <SectionTitle action={…}>Card Binder</SectionTitle>
//     <Panel> … </Panel>
//     <StatTile value={124} label="Total Cards" />
//   </PageShell>
import React from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { colors, fonts } from '../../theme/tokens';

export const vaultWash = [
  'radial-gradient(ellipse 90% 55% at 50% -10%, rgba(212, 175, 55, 0.1), transparent 65%)',
  'linear-gradient(180deg, #1a1c21 0%, #16181c 48%, #121315 100%)',
].join(', ');

// The standard page container: void background, consistent gutters, optional
// page header. Replaces the old `<Box sx={{p:3, background: linear-gradient,
// minHeight:'100vh'}}>` that each screen used to repeat.
export const PageShell = ({ eyebrow, title, subtitle, actions, maxWidth = 1440, children, sx }) => (
  <Box sx={{
    minHeight: '100%',
    backgroundColor: colors.void,
    pl: { xs: 2, lg: 2.5 },
    pr: { xs: 2, md: 2.5 },
    py: { xs: 2, md: 2.5 },
    ...sx,
  }}>
    <Box sx={{ maxWidth, mx: 'auto', width: '100%' }}>
      {(title || eyebrow || actions) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: { xs: 2.5, md: 3 },
            mb: { xs: 2.5, md: 3 },
            pb: { xs: 2, md: 2.5 },
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          <Box sx={{ minWidth: 0, flex: '1 1 280px' }}>
            {eyebrow && (
              <Typography
                sx={{
                  fontFamily: fonts.numeric,
                  fontWeight: 600,
                  fontSize: { xs: '0.8125rem', md: '0.9375rem' },
                  textTransform: 'uppercase',
                  letterSpacing: '0.18em',
                  color: colors.brass,
                  mb: 1,
                }}
              >
                {eyebrow}
              </Typography>
            )}
            {title && (
              <Typography
                component="h1"
                sx={{
                  fontFamily: fonts.impact,
                  fontWeight: 400,
                  fontSize: { xs: '2.5rem', sm: '3rem', md: '3.75rem' },
                  textTransform: 'uppercase',
                  letterSpacing: '0.01em',
                  lineHeight: 0.95,
                  color: colors.textPrimary,
                }}
              >
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography
                sx={{
                  fontFamily: fonts.body,
                  fontWeight: 500,
                  fontSize: { xs: '1rem', md: '1.125rem' },
                  color: colors.textSecondary,
                  mt: 1.25,
                  maxWidth: 520,
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          {actions && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: { xs: 1.5, md: 3.5 },
                alignItems: 'stretch',
                width: { xs: '100%', md: 'auto' },
                flex: { xs: '1 1 100%', md: '0 0 auto' },
                justifyContent: { xs: 'flex-start', md: 'flex-end' },
              }}
            >
              {actions}
            </Box>
          )}
        </Box>
      )}
      {children}
    </Box>
  </Box>
);

// A titled section divider — gold tick + Shapiro caps, optional right-aligned action.
export const SectionTitle = ({ children, action, sx, titleSx, component = 'h2' }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: 2,
      borderBottom: `1px solid ${colors.border}`,
      pb: 1.5,
      mb: 3,
      ...sx,
    }}
  >
    <Typography
      component={component}
      sx={{
        fontFamily: fonts.display,
        fontWeight: 400,
        fontSize: { xs: '1.5rem', md: '1.75rem' },
        textTransform: 'uppercase',
        letterSpacing: '0.02em',
        color: colors.textPrimary,
        display: 'flex',
        alignItems: 'center',
        lineHeight: 1,
        minWidth: 0,
        ...titleSx,
      }}
    >
      <Box
        component="span"
        aria-hidden
        sx={{
          width: 3,
          height: '0.72em',
          mr: 1.25,
          flexShrink: 0,
          backgroundColor: colors.brass,
          borderRadius: '1px',
        }}
      />
      {children}
    </Typography>
    {action && <Box sx={{ flexShrink: 0, minWidth: 0 }}>{action}</Box>}
  </Box>
);

// A surface container — panel color, 1px steel border, hard edges, no shadow.
// Pass `wash` for stage brass radial wash (console / binder surfaces).
// Forwards `component` / event handlers so interactive panels can use ButtonBase.
export const Panel = ({ children, sx, padding = 3, wash = false, component = 'div', ...rest }) => (
  <Box
    component={component}
    sx={{
      backgroundColor: wash ? undefined : colors.panel,
      background: wash ? vaultWash : undefined,
      border: `1px solid ${colors.border}`,
      borderRadius: '2px',
      boxShadow: wash ? 'inset 0 1px 0 rgba(212, 175, 55, 0.14)' : undefined,
      p: padding,
      ...sx,
    }}
    {...rest}
  >
    {children}
  </Box>
);

// A spec-sheet row — uppercase muted label left, value right, hairline rule
// between rows ("tale of the tape" style). Set numeric for Oswald tabular
// figures on numbers, serials, and dates.
export const SpecRow = ({ label, value, numeric, sx }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'baseline',
      justifyContent: 'space-between',
      gap: 2,
      py: 1.25,
      borderBottom: `1px solid ${colors.border}`,
      '&:last-of-type': { borderBottom: 'none', pb: 0.25 },
      ...sx,
    }}
  >
    <Typography
      sx={{
        fontFamily: fonts.body,
        fontWeight: 600,
        fontSize: '1rem',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        color: colors.textSecondary,
        flexShrink: 0,
      }}
    >
      {label}
    </Typography>
    <Typography
      component="div"
      sx={{
        fontFamily: numeric ? fonts.numeric : fonts.body,
        fontWeight: 600,
        fontSize: numeric ? '1.375rem' : '1.1875rem',
        lineHeight: 1.3,
        color: colors.textPrimary,
        textAlign: 'right',
        minWidth: 0,
        overflowWrap: 'anywhere',
        ...(numeric ? { fontFeatureSettings: "'tnum'" } : {}),
      }}
    >
      {value}
    </Typography>
  </Box>
);

// A stat tile — big gold numeral + uppercase label. HUD framed when `framed`.
// Pass `onClick` to make it a navigational control (ButtonBase).
export const StatTile = ({ value, label, framed = false, title, sx, onClick, ariaLabel }) => {
  const framedSx = framed
    ? {
        px: { xs: 2, md: 2.75 },
        py: { xs: 1.5, md: 1.75 },
        minWidth: { xs: 110, md: 132 },
        textAlign: 'center',
        background: vaultWash,
        border: `1px solid ${colors.border}`,
        borderRadius: '2px',
        boxShadow: 'inset 0 1px 0 rgba(212, 175, 55, 0.14)',
      }
    : {};

  const content = (
    <>
      <Typography
        sx={{
          fontFamily: fonts.numeric,
          fontWeight: 600,
          fontSize: { xs: '2.5rem', md: '3.25rem' },
          lineHeight: 1,
          color: colors.brass,
          fontFeatureSettings: "'tnum'",
          letterSpacing: '0.02em',
        }}
      >
        {value}
      </Typography>
      <Typography
        sx={{
          fontFamily: fonts.display,
          fontWeight: 400,
          fontSize: { xs: '0.8125rem', md: '0.875rem' },
          textTransform: 'uppercase',
          letterSpacing: '0.14em',
          color: colors.textSecondary,
          mt: 0.75,
        }}
      >
        {label}
      </Typography>
    </>
  );

  if (onClick) {
    return (
      <ButtonBase
        onClick={onClick}
        title={title}
        aria-label={ariaLabel || label}
        sx={{
          display: 'block',
          ...framedSx,
          transition: 'transform 0.2s ease, border-color 0.2s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            borderColor: colors.brass,
          },
          ...sx,
        }}
      >
        {content}
      </ButtonBase>
    );
  }

  return (
    <Box title={title} sx={{ ...framedSx, ...sx }}>
      {content}
    </Box>
  );
};
